import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { useEvents } from "api/events";
import { useMember } from "api/members";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { ItemDetails } from "components/ItemDetails/ItemDetails";
import { OLTabs } from "components/Tabs/OLTabs";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useParams } from "react-router-dom";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { AttributesDetails } from "./AttributesDetails/AttributesDetails";
import { ComplexAttribute } from "./AttributesDetails/ComplexAttribute";
import { EarnedRewards } from "./EarnedRewards";
import { EarnedUnits } from "./EarnedUnits";
import { MatchedMemberSection } from "./MatchedMember";
import { TriggeredAchievements } from "./TriggeredAchievements";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      alignItems: "center",
      display: "flex",
      fontSize: 16,
      padding: theme.spacing(0, 0, 5),
    },
    paper: { padding: theme.spacing(8) },
    tooltipWrapper: {
      display: "inline-flex",
      alignItems: "center",
    },
    icon: {
      color: theme.gray500,
      height: "16px",
      marginLeft: theme.spacing(4),
      opacity: 0.5,
      width: "16px",
    },
    tooltipTitle: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "24px",
      color: theme.gray800,
    },
    tooltipDesc: {
      whiteSpace: "break-spaces",
      marginTop: theme.spacing(2),
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 400,
    },
    name: {
      color: theme.gray400,
      fontSize: 14,
      lineHeight: theme.spacing(8),
    },
    attributesContainer: {
      display: "flex",
      gap: theme.spacing(4),
      flexWrap: "wrap",
    },
    attributesContainerModal: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(4),
      flexWrap: "wrap",
    },
    attribute: {
      display: "flex",
      alignItems: "center",
      justifyCOntent: "center",
      padding: theme.spacing(4),
      borderRadius: "4px",
      backgroundColor: theme.gray100,
    },
    showMoreBtn: {
      fontSize: "13px",
      textDecoration: "underline",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  }),
);

export function SingleCustomEvent() {
  const classes = useStyles();
  const { message } = useMessage();
  const { id: customEventID } = useParams<{ id: string }>();
  const refChipsContainer = useRef<HTMLDivElement>(null);

  const [attrContainerHeight, setAttrContainerHeight] = useState(0);
  const [isCustomAttributesModalOpen, setIsCustomAttributesModalOpen] =
    useState(false);

  const { useCustomEventDetails } = useEvents();
  const { data: customEventDetails } = useCustomEventDetails(customEventID);
  const { useMembersData } = useMember(customEventDetails?.customerId);
  const { data: membersData } = useMembersData();

  const isEventMatchedWithMember = !!customEventDetails?.customerId;
  const maxChipsContainerHeight = 150;
  const isShowAllAttrBtn = attrContainerHeight > maxChipsContainerHeight;

  const profileData = [
    {
      label: message({ id: "fullName" }),
      value: membersData
        ? `${membersData.firstName} ${membersData.lastName}`
        : "-",
    },
    {
      label: message({ id: "email" }),
      value: membersData ? membersData.email : "-",
    },
    {
      label: message({ id: "tax" }),
      value: membersData ? membersData.company?.nip : "-",
    },
    {
      label: message({ id: "phoneNumber" }),
      value: membersData ? membersData.phone : "-",
    },
    {
      label: message({ id: "loyaltyCardNumber" }),
      value: membersData ? membersData.loyaltyCardNumber : "-",
    },
    {
      label: message({ id: "streetName" }),
      value: membersData ? membersData.address?.street : "-",
    },
    {
      label: message({ id: "postalCode" }),
      value: membersData ? membersData.address?.postal : "-",
    },
    {
      label: message({ id: "buildingNameNumber" }),
      value: membersData ? membersData.address?.address1 : "-",
    },
    {
      label: message({ id: "flatUnitNameNumber" }),
      value: membersData ? membersData.address?.address2 : "-",
    },
    {
      label: message({ id: "city" }),
      value: membersData ? membersData.address?.city : "-",
    },
    {
      label: message({ id: "stateProvince" }),
      value: membersData ? membersData.address?.province : "-",
    },
    {
      label: message({ id: "country" }),
      value: membersData ? membersData.address?.country : "-",
    },
  ];

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "customEvents" }),
      path: links.customEventsList,
    },
  };

  useEffect(() => {
    if (!refChipsContainer.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setAttrContainerHeight(refChipsContainer.current?.clientHeight as number);
    });
    resizeObserver.observe(refChipsContainer.current);
    return () => resizeObserver.disconnect();
  }, [customEventDetails]);
  const [activeTab, setActiveTab] = useState(0);
  const items = [
    { id: 0, name: "all" },
    {
      component: <MatchedMemberSection member={membersData} />,
      id: 1,
      name: "matchedMember",
    },
    {
      component: <EarnedUnits customEventId={customEventID} />,
      id: 2,
      name: "earnedUnits",
    },
    {
      component: <EarnedRewards customEventId={customEventID} />,
      id: 3,
      name: "earnedRewards",
    },
    {
      id: 4,
      component: (
        <TriggeredAchievements
          customEventId={customEventID}
          memberId={customEventDetails?.customerId as string}
        />
      ),
      name: "triggeredAchievements",
    },
  ].filter((value) => Object.keys(value).length !== 0);

  const filteredItems =
    activeTab === 0 ? items : items.filter((item) => item.id === activeTab);

  return (
    <>
      <TopHeader
        title={`${message({ id: "event" })} #${customEventID}`}
        clearLink={links.customEventsList}
        breadcrumbs={breadcrumbs}
      />
      <Grid container spacing={12} mt={0} mb={24}>
        <Grid item xs={6} pt={0}>
          <span className={classes.header}>
            {message({ id: "customEventDetails" })}
          </span>
          <OLPaper noPadding className={classes.paper}>
            <Grid container spacing={8}>
              {customEventDetails ? (
                <>
                  <Grid item sm={12}>
                    <ItemDetails
                      name={
                        <span className={classes.tooltipWrapper}>
                          <span>{message({ id: "status" })}</span>
                          <Tooltip
                            text={
                              <span>
                                <span className={classes.tooltipTitle}>
                                  {message({ id: "status" })}
                                </span>
                                <p className={classes.tooltipDesc}>
                                  {message({ id: "statusEventInfo" })}
                                </p>
                              </span>
                            }
                          >
                            <HelpCircleIcon className={classes.icon} />
                          </Tooltip>
                        </span>
                      }
                      value={message({
                        id: isEventMatchedWithMember ? "matched" : "notMatched",
                      })}
                      data-test-id="event-status"
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <ItemDetails
                      name={message({ id: "customEventDate" })}
                      value={OLDate(customEventDetails.eventDate) || "-"}
                      data-test-id="event-date"
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <ItemDetails
                      name={message({ id: "customEventName" })}
                      value={customEventDetails.eventName || "-"}
                      data-test-id="event-name"
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <ItemDetails
                      name={message({ id: "customEventId" })}
                      value={customEventDetails.customEventId || "-"}
                      data-test-id="custom-event-id"
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <ItemDetails
                      name={message({ id: "eventID" })}
                      value={customEventDetails.eventId || "-"}
                      data-test-id="event-id"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <span className={classes.name}>
                      {message({ id: "attributes" })}
                    </span>
                    <div
                      className={classes.attributesContainer}
                      ref={refChipsContainer}
                      style={{
                        maxHeight: maxChipsContainerHeight + 1,
                        overflow:
                          attrContainerHeight >= maxChipsContainerHeight
                            ? "hidden"
                            : "visible",
                      }}
                    >
                      <AttributesDetails attributes={customEventDetails.body} />
                    </div>
                    {isShowAllAttrBtn && (
                      <Grid item mt={4}>
                        <OLButton
                          className={classes.showMoreBtn}
                          onClick={() => setIsCustomAttributesModalOpen(true)}
                          data-test-id="transaction-details-show-all-button"
                        >
                          {message(
                            { id: "showAllElements" },
                            {
                              elements: Object.keys(customEventDetails.body)
                                .length,
                            },
                          )}
                        </OLButton>
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Skeleton width={100} />
                </Grid>
              )}
            </Grid>
          </OLPaper>
        </Grid>

        <Grid item xs={6} pt={0}>
          <span className={classes.header}>
            <span>{message({ id: "customerDetailsFromCustomEvent" })}</span>
            <Tooltip
              maxWidth="314px"
              text={
                <span>
                  <span className={classes.tooltipTitle}>
                    {message({ id: "customerDetailsFromCustomEvent" })}
                  </span>
                  <p className={classes.tooltipDesc}>
                    {message({ id: "customerDetailCustomEventInfo" })}
                  </p>
                </span>
              }
            >
              <HelpCircleIcon className={classes.icon} />
            </Tooltip>
          </span>
          <OLPaper noPadding className={classes.paper}>
            <Grid container spacing={8}>
              {profileData.map((item) => (
                <Grid
                  item
                  sm={12}
                  key={item.label}
                  data-test-id={kebabCase(item.label)}
                >
                  <ItemDetails
                    name={item.label}
                    value={item.value || "-"}
                    data-test-id={kebabCase(item.label)}
                    data-hj-suppress
                  />
                </Grid>
              ))}
            </Grid>
          </OLPaper>
        </Grid>
        <Grid item xs={12}>
          <OLTabs
            items={items.map((tab) => ({
              id: tab.id?.toString(),
              name: message({ id: tab.name }),
            }))}
            value={activeTab.toString()}
            onChange={(_, id) => {
              setActiveTab(Number(id));
            }}
          />
        </Grid>
        {filteredItems.map((item) => (
          <>
            {item.component && (
              <Grid item xs={12}>
                {item.component}
              </Grid>
            )}
          </>
        ))}
      </Grid>
      <OLModal
        open={!!(customEventDetails?.body && isCustomAttributesModalOpen)}
        header={message({ id: "customAttributes" })}
        submitText={message({ id: "close" })}
        submitAction={() => setIsCustomAttributesModalOpen(false)}
        onClose={() => setIsCustomAttributesModalOpen(false)}
        modalWidth={700}
      >
        <div className={classes.attributesContainerModal}>
          {Object.keys(customEventDetails?.body || {}).map((key, index) => {
            return (
              <ComplexAttribute
                attribute={{
                  name: key,
                  value: customEventDetails?.body[key],
                }}
              />
            );
          })}
        </div>
      </OLModal>
    </>
  );
}
